// src/components/TrialBanner.js
import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, LinearProgress, Chip, Tooltip, IconButton } from '@mui/material';
import { AlertCircle, Clock, Calendar, X, Info, Check, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const TrialBanner = () => {
  const navigate = useNavigate();
  const { currentUser, checkTrialStatus } = useContext(AuthContext);
  const [trialInfo, setTrialInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showFeatures, setShowFeatures] = useState(false);
  
  const handleUpgradeClick = () => {
    console.log('Upgrade button clicked - navigating to /profiles/upgrade');
    navigate('/profiles/upgrade');
  };
  
  const handleDismissClick = () => {
    console.log('Dismiss button clicked');
    setDismissed(true);
  };
  
  useEffect(() => {
    // First check if there's cached trial info in localStorage
    const cachedTrialInfo = localStorage.getItem('trialInfo');
    
    // If we have cached info that's not expired, use it first for quick rendering
    if (cachedTrialInfo) {
      try {
        const parsedInfo = JSON.parse(cachedTrialInfo);
        // Check if the cached data is still valid (less than 1 hour old)
        if (parsedInfo.timestamp && (Date.now() - parsedInfo.timestamp < 60 * 60 * 1000)) {
          setTrialInfo(parsedInfo);
          setLoading(false);
          // Still fetch fresh data in the background
        }
      } catch (e) {
        // Invalid JSON - ignore and fetch fresh data
        localStorage.removeItem('trialInfo');
      }
    }
    
    // Now check trial status directly from AuthContext
    const trialStatus = checkTrialStatus();
    console.log('Trial status from AuthContext:', trialStatus);
    
    if (trialStatus && trialStatus.isTrial) {
      // Add timestamp for cache invalidation
      const contextTrialInfo = {
        ...trialStatus,
        timestamp: Date.now()
      };
      
      // Cache the trial info in localStorage
      localStorage.setItem('trialInfo', JSON.stringify(contextTrialInfo));
      
      setTrialInfo(contextTrialInfo);
      setLoading(false);
    }
    
    // Also fetch the latest trial info from the backend
    const fetchTrialInfo = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        // Try different API endpoints to match your API structure
        let response;
        try {
          response = await axios.get(`${API_URL}/api/profiles/trial-status`);
        } catch (err) {
          try {
            response = await axios.get(`${API_URL}/profiles/trial-status`);
          } catch (innerErr) {
            console.error('Could not fetch trial info:', innerErr);
            setLoading(false);
            return;
          }
        }
        
        if (response.data && response.data.isTrial) {
          // Add timestamp for cache invalidation
          const trialData = {
            ...response.data,
            timestamp: Date.now()
          };
          
          // Cache the trial info in localStorage
          localStorage.setItem('trialInfo', JSON.stringify(trialData));
          
          setTrialInfo(trialData);
        } else {
          setTrialInfo(null);
          // Clear cached trial info if user is not on trial
          localStorage.removeItem('trialInfo');
        }
      } catch (error) {
        console.error('Error fetching trial status:', error);
        // Keep using cached data if available
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrialInfo();
  }, [currentUser, checkTrialStatus]);
  
  // If not on trial, loading, or banner dismissed, don't display anything
  if (!trialInfo || !trialInfo.isTrial || loading || dismissed) {
    return null;
  }
  
  // Calculate days remaining
  const calculateDaysRemaining = () => {
    if (!trialInfo.trialEndDate) return 0;
    
    const endDate = new Date(trialInfo.trialEndDate);
    const today = new Date();
    
    // Reset times to midnight to get accurate day count
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    // Calculate the difference in milliseconds
    const differenceMs = endDate - today;
    
    // Convert to days (1000ms * 60s * 60m * 24h)
    return Math.max(0, Math.ceil(differenceMs / (1000 * 60 * 60 * 24)));
  };
  
  const daysRemaining = calculateDaysRemaining();
  const totalDays = 7; // assuming 7-day trial
  const progress = ((totalDays - daysRemaining) / totalDays) * 100;
  
  // Get color based on days remaining
  const getStatusColor = () => {
    if (daysRemaining <= 1) return 'error';
    if (daysRemaining <= 3) return 'warning';
    return 'primary';
  };
  
  return (
    <Box
      sx={{
        position: 'relative',
        px: 3,
        py: 1.5,
        bgcolor: theme => 
          daysRemaining <= 1 
            ? theme.palette.error.light 
            : (daysRemaining <= 3 ? '#FFF4E5' : '#E3F2FD'),
        borderBottom: '1px solid',
        borderColor: theme => 
          daysRemaining <= 1 
            ? theme.palette.error.main 
            : (daysRemaining <= 3 ? theme.palette.warning.main : theme.palette.primary.light),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {daysRemaining <= 1 ? (
          <AlertCircle color="#d32f2f" size={20} style={{ marginRight: 12 }} />
        ) : (
          <Clock size={20} style={{ marginRight: 12 }} />
        )}
        
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" mr={1}>
              Your free trial {daysRemaining > 0 ? 'period' : 'has ended'}
            </Typography>
            
            <Chip 
              label={daysRemaining > 0 ? `${daysRemaining} days left` : 'Expired'} 
              size="small"
              color={getStatusColor()}
              variant="outlined"
              icon={<Calendar size={14} />}
            />
            
            <Tooltip title="Click to see what's included in your trial">
              <IconButton 
                size="small" 
                color="primary" 
                sx={{ ml: 1 }}
                onClick={() => setShowFeatures(!showFeatures)}
              >
                <Info size={16} />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '150px', mr: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                color={getStatusColor()}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              {daysRemaining > 0 
                ? `Upgrade now to maintain access to all features.` 
                : `Your trial has expired. Upgrade now to regain access.`}
            </Typography>
          </Box>
          
          {/* Compact Feature List - only shown when info icon is clicked */}
          {showFeatures && (
            <Box 
              sx={{ 
                mt: 1.5, 
                display: 'flex', 
                gap: 2,
                p: 1.5,
                bgcolor: 'rgba(255,255,255,0.7)',
                borderRadius: 1,
                flexWrap: 'wrap'
              }}
            >
              <Box sx={{ minWidth: '180px' }}>
                <Typography variant="caption" fontWeight="bold" color="success.main" display="flex" alignItems="center">
                  <Check size={14} style={{ marginRight: 4 }} /> Available in Trial:
                </Typography>
                <Typography variant="caption" display="block" sx={{ ml: 3 }}>• Profile customization</Typography>
                <Typography variant="caption" display="block" sx={{ ml: 3 }}>• QR code preview</Typography>
                <Typography variant="caption" display="block" sx={{ ml: 3 }}>• Email signature design</Typography>
              </Box>
              
              <Box sx={{ minWidth: '180px' }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary" display="flex" alignItems="center">
                  <Star size={14} style={{ marginRight: 4 }} /> Premium Features:
                </Typography>
                <Typography variant="caption" display="block" sx={{ ml: 3 }}>• Export & download</Typography>
                <Typography variant="caption" display="block" sx={{ ml: 3 }}>• QR code analytics</Typography>
                <Typography variant="caption" display="block" sx={{ ml: 3 }}>• Remove watermarks</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button 
          variant="contained" 
          size="small" 
          color={getStatusColor()}
          sx={{ mr: 2 }}
          onClick={handleUpgradeClick}
        >
          Upgrade Now
        </Button>
        
        <Button
          variant="text"
          size="small"
          onClick={handleDismissClick}
          sx={{ p: 0.5, minWidth: 'auto' }}
        >
          <X size={18} />
        </Button>
      </Box>
    </Box>
  );
};

export default TrialBanner;